import { useCallback, useEffect } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { DeeplinkBonusParams, CustomPromotionsEvent } from "@nxplatform/bonuses/entities/common-entities";
import { selectTextPagePromotion } from "@nxplatform/customer-noauth/slices/text-pages.slice";
import { getIsMobile } from "@nxplatform/helpers/helpers-pure";
import { CustomerBasicModal } from "@nxplatform/platform-entities/customer/customer-enum-types";
import { deepLinks } from "@nxplatform/platform-settings/locale-setup.routes";
import { selectDeepLink } from "@nxplatform/platform-settings/locale-setup.selectors";
import { selectCustomerTokenEntities } from "@nxplatform/store-shared/customer/shared-customer-selectors";
import { useWebNavigate, webViewStatus } from "../../hooks/query.hook.js";
import { useOpenCashierModal } from "@nxplatform/customer-cashier/hooks/open-cashier-modal.hook";
const usePromotionsEvents = ({
  isTextPage,
  linkRef,
  openModal,
  changeBonusType,
  navigate,
  deeplink
}) => {
  const isMobile = getIsMobile();
  const isWebView = webViewStatus();
  const { isWebNavigate } = useWebNavigate();
  const { openCashierModal } = useOpenCashierModal();
  const { promotionData, auth } = useSelector(
    (state) => ({ promotionData: selectTextPagePromotion, auth: selectCustomerTokenEntities(state) }),
    shallowEqual
  );
  const { bonusCode, bonusId } = promotionData || {};
  const { getDeepLink } = useSelector((state) => ({
    getDeepLink: (link) => selectDeepLink(state, link)
  }), shallowEqual);
  const doLogin = useCallback(() => {
    if (auth) {
      openCashierModal({ bonusCode: window.promotionBonusId || bonusCode || bonusId || null });
    } else {
      if (isMobile && isWebView && linkRef.current) {
        linkRef.current.href = getDeepLink(deepLinks.accountSignin);
        linkRef.current.click();
        return;
      }
      openModal(CustomerBasicModal.login);
    }
  }, [isMobile, isWebView, getDeepLink, linkRef, auth]);
  const doRegistration = useCallback(() => {
    if (auth) {
      openCashierModal({ bonusCode: window.promotionBonusId || bonusCode || bonusId || null });
    } else {
      if (isMobile && isWebView && linkRef.current) {
        linkRef.current.href = getDeepLink(deepLinks.accountSignup);
        linkRef.current.click();
        return;
      }
      if (isTextPage && window.promotionType) {
        changeBonusType(window.promotionType);
      }
      openModal(CustomerBasicModal.register);
    }
  }, [isMobile, isWebView, getDeepLink, auth]);
  const doNavigate = useCallback(
    (event) => {
      var _a;
      if (!linkRef.current) return;
      const { navigate: elNavigate, deeplink: elDeeplink } = ((_a = event == null ? void 0 : event.target) == null ? void 0 : _a.dataset) || {};
      if (isWebView && !isWebNavigate) {
        linkRef.current.href = deeplink || elDeeplink ? `${getDeepLink(deeplink || elDeeplink)}` : `${getDeepLink(deepLinks.deposit)}?${DeeplinkBonusParams.bonusId}=${window.promotionBonusId}`;
        linkRef.current.click();
        return;
      }
      if (isWebNavigate || (navigate || elNavigate)) {
        linkRef.current.href = navigate || elNavigate;
        linkRef.current.click();
        return;
      }
      openCashierModal({ bonusCode: window.promotionBonusId || bonusCode || bonusId || null });
    },
    [isMobile, isWebView, isWebNavigate, getDeepLink, linkRef, bonusCode, bonusId, navigate, deeplink]
  );
  useEffect(() => {
    document.addEventListener(CustomPromotionsEvent.login, doLogin);
    document.addEventListener(
      CustomPromotionsEvent.registration,
      doRegistration
    );
    document.addEventListener(CustomPromotionsEvent.navigate, doNavigate);
    return () => {
      document.removeEventListener(CustomPromotionsEvent.login, doLogin);
      document.removeEventListener(
        CustomPromotionsEvent.registration,
        doRegistration
      );
      document.removeEventListener(CustomPromotionsEvent.navigate, doNavigate);
    };
  }, [doLogin, doRegistration, doNavigate]);
  return {
    doLogin,
    doRegistration,
    doNavigate
  };
};
export {
  usePromotionsEvents
};
